import type { InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { X, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  /** Ícone decorativo à esquerda (pointer-events-none). */
  iconLeft?: ReactNode
  /** Ícone decorativo à direita (pointer-events-none). */
  iconRight?: ReactNode
  /**
   * Ação clicável à direita (botão com onClick). Substitui visualmente o `X` de
   * limpar e o ícone decorativo `iconRight` quando presente. Use para coisas
   * como "lupa de buscar CEP" dentro do próprio Input.
   */
  actionRight?: ReactNode
  wrapperClassName?: string
  /**
   * Mostra um botão X para limpar quando o campo está preenchido (regra global).
   * Default: true. Desabilite com `clearable={false}` em campos type=date/time/file.
   */
  clearable?: boolean
  /**
   * Em `type="password"`, mostra um botão de olhinho que alterna a visibilidade
   * da senha. Default: true (quando type=password). Ignorado para outros tipos.
   */
  revealable?: boolean
}

/**
 * Input padrão do sistema.
 *  - autoComplete OFF por default (regra global), sobrescrevível.
 *  - **Botão X aparece à direita quando há valor** (regra global). Coexiste com
 *    `actionRight` (lupa/etc.) e com o olhinho de senha — todos renderizados
 *    em linha no canto direito.
 *  - Em `type="password"`, mostra olhinho para alternar visibilidade.
 *  - `actionRight`: botão clicável customizado à direita (ex.: lupa de CEP).
 */
export const Input = forwardRef<HTMLInputElement, Props>(function Input(
  { label, error, hint, iconLeft, iconRight, actionRight, className, wrapperClassName,
    clearable = true, revealable = true, required, type, ...rest },
  ref,
) {
  const hasError = Boolean(error)

  // Toggle de visualização de senha
  const [revealed, setRevealed] = useState(false)
  const isPasswordField = type === 'password'
  const effectiveType   = isPasswordField && revealed ? 'text' : type

  // === Detecção de "tem valor" ===
  // Funciona em 3 modos:
  //   1. CONTROLLED (passou `value`): lê direto da prop.
  //   2. UNCONTROLLED com RHF register (passou ref+onChange mas não value):
  //      mantém estado interno atualizado por:
  //      a) `onChange` interceptado (digitação do usuário, máscaras)
  //      b) leitura do ref a cada re-render (cobre form.reset(), setValue
  //         programático etc. — o pai re-renderiza, esse useEffect roda).
  //   3. UNCONTROLLED com defaultValue: estado inicial vem do defaultValue.
  const isControlled = rest.value !== undefined
  const [internalHasValue, setInternalHasValue] = useState(() => {
    if (rest.value !== undefined && rest.value !== null) return String(rest.value) !== ''
    if (rest.defaultValue !== undefined && rest.defaultValue !== null) return String(rest.defaultValue) !== ''
    return false
  })

  // Tipos onde X de limpar atrapalha a UX nativa.
  const skipClearable = ['date', 'time', 'datetime-local', 'month', 'week', 'file', 'checkbox', 'radio', 'range', 'color'].includes(type ?? '')

  const hasValue = isControlled
    ? (rest.value !== undefined && rest.value !== null && String(rest.value) !== '')
    : internalHasValue

  // === Slots do canto direito — empilháveis ===
  // O X de limpar é INDEPENDENTE dos demais e sempre aparece quando há valor.
  const showClear  = clearable && hasValue && !rest.disabled && !rest.readOnly && !skipClearable && typeof rest.onChange === 'function'
  const showReveal = isPasswordField && revealable
  const showAction = !!actionRight
  // iconRight só aparece quando NÃO há nenhuma ação ou clear pra exibir.
  const showIconR  = !showAction && !showReveal && !showClear && !!iconRight

  // Padding direito proporcional à quantidade de elementos no canto direito.
  const rightSlots = (showClear ? 1 : 0) + (showReveal ? 1 : 0) + (showAction ? 1 : 0) + (showIconR ? 1 : 0)
  const rightPadClass = ['', 'pr-9', 'pr-16', 'pr-24', 'pr-32'][rightSlots] ?? 'pr-32'

  // Para disparar um onChange "limpar" usando o handler do consumidor:
  const innerRef = useRef<HTMLInputElement | null>(null)
  function setRefs(el: HTMLInputElement | null) {
    innerRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el
  }

  // Sincroniza `internalHasValue` com o valor real do DOM a cada render.
  // Necessário para casos onde o valor muda PROGRAMATICAMENTE sem disparar
  // change/input (ex.: `form.reset()` do react-hook-form sobre uncontrolled).
  // `setState` só dispara quando o boolean MUDA, então não há loop infinito.
  useEffect(() => {
    if (isControlled) return
    const current = innerRef.current?.value ?? ''
    const next = current !== ''
    if (next !== internalHasValue) setInternalHasValue(next)
  })

  function onChangeInternal(e: React.ChangeEvent<HTMLInputElement>) {
    if (!isControlled) setInternalHasValue(e.target.value !== '')
    rest.onChange?.(e)
  }

  function clear() {
    const input = innerRef.current
    if (!input) return
    // Usa o setter nativo para que o React detecte a mudança via input event.
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
    setter?.call(input, '')
    input.dispatchEvent(new Event('input', { bubbles: true }))
    // Garante feedback imediato no estado interno (mesmo se o consumidor
    // ignorar o evento). Em controlled, o `value` prop muda no próximo render.
    if (!isControlled) setInternalHasValue(false)
    input.focus()
  }

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-xs font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
      )}
      <div className={cn(
        'relative flex items-center bg-white rounded-md ring-1 transition',
        hasError ? 'ring-rose-300 focus-within:ring-rose-500' : 'ring-gray-200 focus-within:ring-brand-500 focus-within:ring-2',
      )}>
        {iconLeft && <span className="absolute left-3 text-gray-400 pointer-events-none">{iconLeft}</span>}
        <input
          ref={setRefs}
          type={effectiveType}
          {...rest}
          // REGRA GLOBAL: autocomplete SEMPRE off (inclusive login). Fica DEPOIS
          // do spread para não poder ser sobrescrito por nenhum chamador.
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          // Sobrescreve `onChange` (vem em `rest` via spread) para também
          // sincronizar o estado interno que controla a exibição do X.
          onChange={onChangeInternal}
          className={cn(
            'w-full bg-transparent rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none',
            'px-3 py-2',
            iconLeft && 'pl-9',
            rightPadClass,
            className,
          )}
        />

        {/* Cluster de ações à direita — empilhadas em flex.
            Ordem visual: [X clear] [olhinho password] [actionRight customizado]
            iconRight (decorativo) fica em modo absolute quando é o único elemento. */}
        {(showClear || showReveal || showAction) && (
          <div className="absolute right-1.5 flex items-center gap-0.5">
            {showClear && (
              <button
                type="button"
                tabIndex={-1}
                aria-label="Limpar"
                onClick={clear}
                className="cursor-pointer inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
              >
                <X size={14} />
              </button>
            )}
            {showReveal && (
              <button
                type="button"
                tabIndex={-1}
                aria-label={revealed ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => setRevealed(r => !r)}
                className="cursor-pointer inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
              >
                {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            )}
            {showAction && <>{actionRight}</>}
          </div>
        )}

        {showIconR && (
          <span className="absolute right-3 text-gray-400 pointer-events-none">{iconRight}</span>
        )}
      </div>
      {(error || hint) && (
        <span className={cn('text-[11px] mt-1', hasError ? 'text-rose-600' : 'text-gray-500')}>
          {error || hint}
        </span>
      )}
    </div>
  )
})
